import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Row, Select, Space, Table, Tabs, Tag } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import ModalTextForm from '../../components/modal/ModalTextForm';
import { validateMessages } from '../../helpers/common';
import { SoHieuVanBan, loaiSoHieuVanBan } from '../../models/setting/Setting';
import { layDSSoHieuVanBan, luuSoHieuVanBan, xoaSoHieuVanBan } from '../../services/setting';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';

/// Ghép thử số hiệu ngay trên form để người dùng thấy trước khi lưu
const dinhDangSoHieu = (soThuTu: number, nam: number, maVanBan: string, doDaiSo: number) => {
    const doDai = doDaiSo > 0 ? doDaiSo : 2;
    return String(soThuTu).padStart(doDai, '0') + "/" + (nam || "") + "/" + (maVanBan || "");
};

const SoHieuVanBanTab = () => {
    const [data, setData] = useState<Array<SoHieuVanBan>>([]);
    const [showModalChiTiet, setShowModalChiTiet] = useState<boolean>(false);
    const [banGhiXoa, setBanGhiXoa] = useState<SoHieuVanBan | null>(null);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    /// Theo dõi form để dựng phần xem trước số hiệu sẽ cấp tiếp theo
    const idDangSua = Form.useWatch('id', form);
    const namNhap = Form.useWatch('nam', form);
    const maVanBanNhap = Form.useWatch('maVanBan', form);
    const soHienTaiNhap = Form.useWatch('soHienTai', form);
    const doDaiSoNhap = Form.useWatch('doDaiSo', form);

    /// Bộ đếm đang sửa không được hạ xuống dưới số đã cấp cho văn bản thật
    const soToiThieu = data.find(x => x.id === idDangSua)?.soLonNhatDaDung || 0;

    const setFormDetailModal = (value: SoHieuVanBan | null) => {
        form.setFieldsValue({
            "id": value?.id || 0,
            "loaiVanBan": value?.loaiVanBan || loaiSoHieuVanBan[0].value,
            "nam": value?.nam || dayjs().year(),
            "maVanBan": value?.maVanBan || "HĐLĐ-QĐTPT",
            "soHienTai": value?.soHienTai || 0,
            "doDaiSo": value?.doDaiSo || 2,
        });
        setShowModalChiTiet(true);
    };

    const layDuLieu = async () => {
        try {
            setIsLoadingTable(true);
            let rs = await layDSSoHieuVanBan();
            setData(rs);
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, không lấy được danh sách số hiệu văn bản!');
        }
        setIsLoadingTable(false);
    };

    const onFinish = async (values: any) => {
        try {
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);
            await luuSoHieuVanBan({
                id: values.id || 0,
                loaiVanBan: values.loaiVanBan,
                nam: values.nam,
                maVanBan: (values.maVanBan || "").trim(),
                soHienTai: values.soHienTai || 0,
                doDaiSo: values.doDaiSo || 2,
            });
            setShowModalChiTiet(false);
            toast.success('Lưu bộ đếm số hiệu văn bản thành công!');
            setReloadPage(true);
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, lưu không thành công!');
        }
        dispatch(hideLoadingScreen());
        setIsLoadingBtn(false);
    };

    const onDelete = async () => {
        if (!banGhiXoa) {
            return;
        }

        try {
            setIsLoadingBtn(true);
            await xoaSoHieuVanBan(banGhiXoa.id);
            toast.success('Xoá bộ đếm số hiệu văn bản thành công!');
            setReloadPage(true);
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, xoá không thành công!');
        }
        setBanGhiXoa(null);
        setIsLoadingBtn(false);
    };

    useEffect(() => {
        if (reloadPage) {
            layDuLieu();
            setReloadPage(false);
        }
    }, [reloadPage]);

    return <section>
        {banGhiXoa && <ModalTextForm show={!!banGhiXoa}
            title="Xoá bộ đếm số hiệu văn bản"
            handleClose={() => setBanGhiXoa(null)}
            handleSave={onDelete}>
            Xác nhận xoá bộ đếm số hiệu {banGhiXoa.tenLoaiVanBan} năm {banGhiXoa.nam}?
        </ModalTextForm>}
        {showModalChiTiet && <ModalTextForm title="Bộ đếm số hiệu văn bản"
            hideFooter
            show={showModalChiTiet}
            handleClose={() => setShowModalChiTiet(false)}
        >
            <Form
                layout="vertical"
                validateMessages={validateMessages}
                onFinish={onFinish}
                form={form}
            >
                <Form.Item hidden name="id"><Input /></Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Loại văn bản"
                            name="loaiVanBan"
                            rules={[{ required: true, message: 'Vui lòng chọn loại văn bản' }]}
                        >
                            <Select options={loaiSoHieuVanBan} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Năm cấp số"
                            name="nam"
                            tooltip="Mỗi năm một bộ đếm, số thứ tự chạy lại từ 1 khi sang năm mới"
                            rules={[{ required: true, message: 'Vui lòng nhập năm cấp số' }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={2000} max={2100} precision={0} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Mã văn bản"
                            name="maVanBan"
                            tooltip="Phần đứng cuối số hiệu, ví dụ HĐLĐ-QĐTPT"
                            rules={[
                                { required: true, message: 'Vui lòng nhập mã văn bản' },
                                { max: 50, message: 'Mã văn bản không được quá 50 ký tự' },
                                { pattern: /^[^/]*$/, message: 'Mã văn bản không được chứa ký tự /' },
                            ]}
                        >
                            <Input placeholder="HĐLĐ-QĐTPT" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Độ dài phần số"
                            name="doDaiSo"
                            tooltip="Số chữ số tối thiểu: 2 cho ra 01, 02 ... 99, 100"
                            rules={[{ required: true, message: 'Vui lòng nhập độ dài phần số' }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={1} max={10} precision={0} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Số đã cấp gần nhất"
                            name="soHienTai"
                            tooltip="Văn bản kế tiếp nhận số này cộng 1. Để 0 nếu năm đó chưa cấp số nào"
                            rules={[
                                { required: true, message: 'Vui lòng nhập số đã cấp gần nhất' },
                                {
                                    type: 'number',
                                    min: soToiThieu,
                                    message: 'Năm này đã cấp đến số ' + soToiThieu + ', đặt nhỏ hơn sẽ cấp trùng số',
                                },
                            ]}
                        >
                            <InputNumber style={{ width: "100%" }} min={0} precision={0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Số hiệu cấp tiếp theo">
                            <Tag color="blue" style={{ fontSize: 14, padding: "4px 8px" }}>
                                {dinhDangSoHieu((soHienTaiNhap || 0) + 1, namNhap, maVanBanNhap, doDaiSoNhap)}
                            </Tag>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <span className="text-muted">
                            Số hiệu có dạng số/năm/mã. Hệ thống cấp số khi văn thư ban hành văn bản,
                            văn bản đã có số thì giữ nguyên số cũ.
                        </span>
                    </Col>
                </Row>
                <Form.Item className="mt-3">
                    <Button type="primary" loading={isLoadingBtn} className="float-right" htmlType="submit" icon={<SaveOutlined rev="" />}>
                        Lưu
                    </Button>
                </Form.Item>
            </Form>
        </ModalTextForm>}
        <Tabs
            defaultActiveKey="1"
            tabBarExtraContent={
                <Button
                    className="hrm-btn hrm-btn-warning"
                    onClick={() => setFormDetailModal(null)}
                    icon={<PlusOutlined rev="" />}
                >
                    Thêm mới
                </Button>
            }
            items={[
                {
                    key: '1',
                    label: 'Số hiệu văn bản',
                    children:
                        <div className="card">
                            <Table
                                loading={isLoadingTable}
                                locale={{ emptyText: "Không có dữ liệu" }}
                                dataSource={data.map((item: any, index) => { item.key = index; return item; })}
                                pagination={false}
                            >
                                <Column
                                    width="7%"
                                    title="STT"
                                    key="STT"
                                    align="center"
                                    render={(value, record, index) => (<>{index + 1}</>)}
                                />
                                <Column title="Loại văn bản" dataIndex="tenLoaiVanBan" key="tenLoaiVanBan" />
                                <Column title="Năm" dataIndex="nam" key="nam" align="center" />
                                <Column title="Mã văn bản" dataIndex="maVanBan" key="maVanBan" align="center" />
                                <Column
                                    title="Số hiệu gần nhất"
                                    key="soHieuGanNhat"
                                    align="center"
                                    render={(record: SoHieuVanBan) => (
                                        record.soHieuGanNhat
                                            ? <>{record.soHieuGanNhat}</>
                                            : <span className="text-muted">Chưa cấp số</span>
                                    )}
                                />
                                <Column
                                    title="Số hiệu cấp tiếp theo"
                                    key="soHieuTiepTheo"
                                    align="center"
                                    render={(record: SoHieuVanBan) => (<Tag color="blue">{record.soHieuTiepTheo}</Tag>)}
                                />
                                <Column
                                    title="Văn bản đã cấp số"
                                    key="soVanBanDaCap"
                                    align="center"
                                    render={(record: SoHieuVanBan) => (<>{record.soVanBanDaCap}</>)}
                                />
                                <Column
                                    title="Cập nhật lần cuối"
                                    key="ngayCapNhat"
                                    align="center"
                                    render={(record: SoHieuVanBan) => (
                                        <>{record.ngayCapNhat ? dayjs(record.ngayCapNhat).format("DD/MM/YYYY HH:mm") : ""}</>
                                    )}
                                />
                                <Column
                                    title=""
                                    key="action"
                                    align="center"
                                    render={(record: SoHieuVanBan) => (
                                        <Space>
                                            <Button type="primary" size={"small"} title="Sửa"
                                                icon={<EditOutlined rev="" />}
                                                onClick={() => setFormDetailModal(record)}
                                            >
                                                Sửa
                                            </Button>
                                            {/* Bộ đếm đã cấp số mà xoá đi thì năm sau cấp lại từ đầu và trùng số cũ */}
                                            {record.soHienTai === 0 &&
                                                <Button type="primary" danger size={"small"} title="Xóa"
                                                    icon={<DeleteOutlined rev="" />}
                                                    onClick={() => setBanGhiXoa(record)}
                                                >
                                                    Xóa
                                                </Button>}
                                        </Space>
                                    )}
                                />
                            </Table>
                        </div>
                },
            ]}>
        </Tabs>
    </section>
}

export default SoHieuVanBanTab;
